import { RecordingBackendClient, } from '@streamr/tv-engine/~/recording';
import { executeSoon } from '@streamr/tv-utils';
import { hexToBinary, } from '@streamr/utils';
import { showToast } from '../../components/popups/ToastPopup';
import { StreamrAppApi } from '../../engine/StreamrAppApi';
import { formRecordingUrls } from '../../utils/routes';
import { ScheduleService } from '../schedule/ScheduleService';
import { StreamrAppService } from '../streamrapp/StreamrAppService';
import { RecordingServiceOutput } from './RecordingServiceOutput';
import { createRecordingSigner, findBroadcasterPrivateKey, } from './recordingKeys';
const RECORDINGS_LIST_LIMIT = 15;
function randomRecordingId() {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
/**
 * Recording of broadcasts. The host's browser records what it publishes
 * (see src/engine/streamrapp/recording) and uploads it in the background;
 * this service starts and stops that, follows the uploads, and lists and
 * deletes recordings through the recording backend.
 */
export class RecordingService {
    static instance;
    recordingsListAbortController;
    activeRecording;
    recordingsList;
    warnsBeforeUnload = false;
    unfinished = new Set();
    static getInstance() {
        if (!RecordingService.instance) {
            RecordingService.instance = new RecordingService();
        }
        return RecordingService.instance;
    }
    static getNotificationInterface() {
        return RecordingService.getInstance();
    }
    static getOutput() {
        return RecordingServiceOutput.getReactiveOutput();
    }
    output;
    constructor() {
        this.output = new RecordingServiceOutput();
    }
    async init() {
        const available = formRecordingUrls() !== undefined;
        this.output.setIsAvailable(available);
        if (available) {
            // does not hold the start of the app back
            executeSoon(() => this.resumeUnfinishedUploads());
        }
    }
    /**
     * A recording that was being uploaded when its page was closed is
     * finished here, by whichever page of the app opens next.
     */
    async resumeUnfinishedUploads() {
        const credentials = ScheduleService.getInstance()
            .getBroadcasterCredentials()
            .map((c) => ({
            broadcasterUserId: c.broadcasterUserId,
            broadcasterPrivateKey: hexToBinary(c.broadcasterPrivateKey),
        }));
        if (credentials.length === 0) {
            return;
        }
        try {
            const resumed = await StreamrAppApi.resumeRecordingUploads(credentials, (state) => this.notifyRecordingState(state));
            if (resumed.length > 0) {
                console.log('[RecordingService] finishing uploads', resumed);
            }
        }
        catch (error) {
            console.warn('[RecordingService] resume failed', error);
        }
    }
    notifyStartRecording(broadcast, visibility) {
        const urls = formRecordingUrls();
        if (!urls) {
            showToast('Recording is not available here');
            return;
        }
        const recordingId = randomRecordingId();
        this.activeRecording = { recordingId, visibility, broadcast };
        this.output.setActiveRecording({
            recordingId,
            visibility,
            initializedAt: Date.now(),
            status: 'initializing',
        });
        executeSoon(() => this.startRecording(urls.backendUrl));
    }
    async startRecording(backendUrl) {
        const active = this.activeRecording;
        if (!active) {
            return;
        }
        try {
            await StreamrAppService.getInstance().startRecording({
                recordingId: active.recordingId,
                visibility: active.visibility,
                backendUrl,
            });
        }
        catch (error) {
            console.error('[RecordingService] start failed', error);
            if (this.activeRecording?.recordingId === active.recordingId) {
                this.output.setActiveRecordingStatus('error', error instanceof Error ? error.message : String(error));
            }
        }
    }
    notifyStopRecording(doneCallback) {
        const active = this.activeRecording;
        if (!active) {
            this.output.setActiveRecording(undefined);
            doneCallback?.();
            return;
        }
        this.output.setActiveRecordingStatus('stopping');
        executeSoon(() => StreamrAppService.getInstance().stopRecording(), (error) => {
            console.error('[RecordingService] stop failed', error);
        }, () => {
            // the upload goes on; the indicator is free for the next one
            if (this.activeRecording?.recordingId === active.recordingId) {
                this.activeRecording = undefined;
                this.output.setActiveRecording(undefined);
            }
            doneCallback?.();
        });
    }
    notifyRetryInitialization() {
        const active = this.activeRecording;
        const urls = formRecordingUrls();
        if (!active || !urls) {
            this.activeRecording = undefined;
            this.output.setActiveRecording(undefined);
            return;
        }
        // a failed recording keeps what it has under its id: start afresh
        this.notifyStartRecording(active.broadcast, active.visibility);
    }
    /** Called with what the engine reports about a recording. */
    notifyRecordingState(state) {
        const isActive = this.activeRecording?.recordingId === state.recordingId;
        switch (state.status) {
            case 'recording':
                if (isActive) {
                    this.output.setActiveRecordingStatus('recording');
                }
                break;
            case 'uploading':
                this.trackUpload(state);
                break;
            case 'error':
                if (isActive) {
                    this.output.setActiveRecordingStatus('error', state.error?.message);
                }
                // what was recorded before the failure is still uploaded
                this.trackUpload(state);
                break;
            case 'complete':
                this.untrackUpload(state.recordingId);
                if (!isActive) {
                    showToast('Recording saved');
                }
                break;
        }
    }
    trackUpload(state) {
        if (state.recordedBytes === 0) {
            return;
        }
        if (state.status === 'error' &&
            state.uploadedBytes >= state.recordedBytes) {
            this.untrackUpload(state.recordingId);
            return;
        }
        this.unfinished.add(state.recordingId);
        this.output.setUpload({
            recordingId: state.recordingId,
            recordedBytes: state.recordedBytes,
            uploadedBytes: state.uploadedBytes,
            paused: state.uploadPaused,
            error: state.error?.code === 'backend-rejected'
                ? state.error.message
                : undefined,
        });
        this.updateUnloadWarning();
    }
    untrackUpload(recordingId) {
        this.unfinished.delete(recordingId);
        this.output.removeUpload(recordingId);
        this.updateUnloadWarning();
    }
    onBeforeUnload = (event) => {
        // closing loses nothing: the next page finishes the upload. It is
        // still worth a question, the recording is not online before that.
        event.preventDefault();
    };
    updateUnloadWarning() {
        const needed = this.unfinished.size > 0;
        if (needed === this.warnsBeforeUnload) {
            return;
        }
        this.warnsBeforeUnload = needed;
        if (needed) {
            window.addEventListener('beforeunload', this.onBeforeUnload);
        }
        else {
            window.removeEventListener('beforeunload', this.onBeforeUnload);
        }
    }
    notifyFetchRecordingsList(broadcasterUserId, streamId, includePrivate) {
        this.recordingsList = {
            broadcasterUserId,
            streamId,
            includePrivate,
            shown: 0,
        };
        this.output.initRecordingsList();
        executeSoon(() => this.listRecordings());
    }
    notifyFetchMoreRecordings() {
        executeSoon(() => this.listRecordings());
    }
    notifyResetRecordingsList() {
        this.recordingsListAbortController?.abort();
        this.recordingsList = undefined;
        this.output.clearRecordingsList();
    }
    notifyDeleteRecording(recording) {
        const urls = formRecordingUrls();
        const privateKey = findBroadcasterPrivateKey(recording.streamId);
        if (!urls || !privateKey) {
            showToast('Only the broadcaster can delete a recording');
            return;
        }
        executeSoon(async () => {
            await new RecordingBackendClient({
                backendUrl: urls.backendUrl,
            }).deleteRecording(createRecordingSigner(recording.broadcasterUserId, privateKey), recording.recordingId, recording.streamId);
            this.output.removeRecordingsListEntry(recording.recordingId);
            if (this.recordingsList?.all) {
                this.recordingsList.all = this.recordingsList.all.filter((r) => r.recordingId !== recording.recordingId);
                this.recordingsList.shown = Math.max(0, this.recordingsList.shown - 1);
            }
            showToast('Recording deleted');
        }, (error) => {
            console.error('[RecordingService] delete failed', error);
            showToast('The recording could not be deleted');
        });
    }
    async listRecordings() {
        const list = this.recordingsList;
        if (!list) {
            return;
        }
        this.recordingsListAbortController?.abort();
        const controller = new AbortController();
        this.recordingsListAbortController = controller;
        if (list.shown > 0) {
            this.output.updateRecordingsListStatus('loadingMore');
        }
        try {
            list.all ??= await this.fetchRecordings(list, controller.signal);
            if (controller.signal.aborted || this.recordingsList !== list) {
                return;
            }
            const page = list.all.slice(list.shown, list.shown + RECORDINGS_LIST_LIMIT);
            list.shown += page.length;
            this.output.updateRecordingsListEntries(page);
            this.output.updateRecordingsListHasMore(list.shown < list.all.length);
            this.output.updateRecordingsListStatus('loaded');
        }
        catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                return;
            }
            console.error('Error fetching recordings list:', error);
            if (this.recordingsList === list) {
                this.output.updateRecordingsListStatus('error');
                this.recordingsList = undefined;
            }
        }
        finally {
            if (this.recordingsListAbortController === controller) {
                this.recordingsListAbortController = undefined;
            }
        }
    }
    async fetchRecordings(list, signal) {
        const urls = formRecordingUrls();
        if (!urls) {
            return [];
        }
        const client = new RecordingBackendClient({
            backendUrl: urls.backendUrl,
        });
        const privateKey = list.includePrivate
            ? findBroadcasterPrivateKey(list.streamId)
            : undefined;
        const [publicEntries, privateEntries] = await Promise.all([
            client.listPublicRecordings(list.broadcasterUserId, list.streamId, signal),
            privateKey
                ? client.listPrivateRecordings(createRecordingSigner(list.broadcasterUserId, privateKey), list.streamId, signal)
                : Promise.resolve([]),
        ]);
        return [...publicEntries, ...privateEntries]
            .filter((entry) => entry.status === 'complete')
            .map((entry) => ({
            recordingId: entry.recordingId,
            streamId: list.streamId,
            broadcasterUserId: list.broadcasterUserId,
            timestamp: entry.createdAtMs,
            isPublic: entry.visibility === 'public',
            isReady: true,
            durationUs: entry.durationUs ?? 0,
            contentLength: entry.byteTotal ?? 0,
            partCount: entry.partCount ?? 1,
            hasThumbnail: entry.hasThumbnail ?? false,
        }))
            .sort((a, b) => b.timestamp - a.timestamp);
    }
}
